import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getData, addData, updateData, getpayment, getConferenceEvent, getoccupancyType, getReceiptId, getprogramManager, getfreeParticipant } from '../../../services/event/accommodationForm/accommodationServices';
import { Button, Form, Card, Badge } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import Header from '../../../components/Layout/Header';
import Footer from '../../../components/Layout/Footer';
import Sidebar from '../../../components/Layout/Sidebar';
import Spinner from '../../../components/Spinner';
import Swal from 'sweetalert2';

const subDomain = localStorage.getItem('sub_domain') || '';

const INITIAL_FORM = {
  id: '',
  accommodation_id: '',
  pr_id: [],
  first_name: '',
  last_name: '',
  occupancy: '',
  no_of_nights: '',
  accompany_name: '',
  check_in: '',
  check_out: '',
  remarks: '',
  note: '',
  notes: [],
  hotel_name: '',
  conf_shortname: '',
  sub_domain: '',
  series: '',
  venue: '',
  confirmation_no: '',
  verified: '',
  updatedAt: '',
  full_name: '',
  accommodation_status: '',
};

const Accommodation = () => {
  const [dataList, setDataList]                             = useState([]);
  const [names, setNames]                                   = useState([]);
  const [form, setForm]                                     = useState(INITIAL_FORM);
  const [formMode, setFormMode]                             = useState('');
  const [searchText, setSearchText]                         = useState('');
  const [currentPage, setCurrentPage]                       = useState(1);
  const [errors, setErrors]                                 = useState({});
  const [loading, setLoading]                               = useState(true);
  const [addParticipantMode, setAddParticipantMode]         = useState(false);
  const [additionalParticipants, setAdditionalParticipants] = useState([]);
  const [selectedParticipant, setSelectedParticipant]       = useState(null);
  const [submitLoading, setSubmitLoading]                   = useState(false);
  const [conferenceInfo, setConferenceInfo]                 = useState(null);
  const [dropdownData, setDropdownData]                     = useState({ occupancyType: [] });

  const MAX_PARTICIPANTS = 5;
  const formRef = useRef(null);

  const statusClassMap = {
    WAITING:   'bg-warning',
    PENDING:   'bg-primary',
    STAGING:   'bg-info',
    VERIFIED:  'bg-success',
    CANCELLED: 'bg-danger',
    DEFAULT:   'bg-secondary',
  };

  // ─── Fetchers (all unchanged) ─────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    try {
      const data = await getData({ sub_domain: subDomain });
      setDataList(data);
    } catch (error) { console.error('Failed to fetch data:', error); }
  }, []);

  const fetchReceiptId = useCallback(async (type) => {
    try {
      const result = await getReceiptId({ sub_domain: subDomain, type });
      return result?.accommodation_id || '';
    } catch (error) { console.error('Error fetching accommodation ID:', error); return ''; }
  }, []);

  const fetchDropdownData = useCallback(async () => {
    try {
      const [occupancy] = await Promise.all([getoccupancyType()]);
      setDropdownData({ occupancyType: occupancy });
    } catch (error) { console.error('Error fetching dropdown data:', error); }
  }, []);

  const fetchConferenceDetails = useCallback(async () => {
    try {
      const data = await getConferenceEvent({ sub_domain: subDomain });
      if (!data || data.length === 0) return;
      const eventInfo         = data[0];
      const resolvedSeries    = eventInfo.series     || '';
      const resolvedSubDomain = eventInfo.sub_domain || '';
      const resolvedHotelName = eventInfo.hotel_name || '';
      const resolvedShortname = eventInfo.shortname  || '';
      const resolvedVenue     = eventInfo.venue      || '';
      setConferenceInfo({
        series: resolvedSeries, sub_domain: resolvedSubDomain,
        hotel_name: resolvedHotelName, conf_shortname: resolvedShortname, venue: resolvedVenue,
      });
      if (resolvedSeries) {
  try {
    const [paymentData, freeParticipantData] = await Promise.all([
      getpayment({
        sub_domain: subDomain,
        accommodation: 1,
        series: resolvedSeries,
        pdf_status: 1,
      }),
      getfreeParticipant({
        sub_domain: subDomain,
        accommodation: 1,
        series: resolvedSeries,
        free_status: 1,
      }),
    ]);

    const paymentList = (paymentData || []).map(item => ({
      payment_id:           item.payment_id,
      billing_full_name:    item.billing_full_name,
      date_of_payment:      item.date_of_payment,
      accommodation_status: item.accommodation_status,
      occupancy_type:       item.occupancy_type,
      total_no_of_nights:   item.total_no_of_nights,
      reg_category:         item.reg_category,
      source:               'payment',
    }));

    const freeList = (freeParticipantData || []).map(item => ({
      payment_id:           item.payment_id,
      billing_full_name:    item.billing_full_name,
      date_of_payment:      item.createdAt,          // freeParticipant has no date_of_payment
      accommodation_status: item.accommodation_status,
      occupancy_type:       item.occupancy_type,
      total_no_of_nights:   item.total_no_of_nights,
      reg_category:         item.reg_category,
      source:               'free',
    }));

    setNames([...paymentList, ...freeList]);
  } catch (err) {
    console.error('Failed to fetch participant data:', err);
  }
}
    } catch (error) { console.error('Failed to fetch conference event details:', error); }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchData();
      await fetchDropdownData();
      await fetchConferenceDetails();
      await new Promise(r => setTimeout(r, 1000));
      setLoading(false);
    };
    init();
  }, [fetchData, fetchDropdownData, fetchConferenceDetails]);

  // ─── Helpers (all unchanged) ──────────────────────────────────────────────

  const usedPaymentIds = useMemo(() =>
    dataList.flatMap(d => Array.isArray(d.pr_id) ? d.pr_id : []),
  [dataList]);

  const availableParticipants = useMemo(() => {
    const currentIds = [
      ...(form.pr_id || []),
      ...additionalParticipants.map(p => p.pr_id),
    ].map(String);
    return names.filter(item =>
      !usedPaymentIds.map(String).includes(String(item.payment_id)) &&
      !currentIds.includes(String(item.payment_id))
    );
  }, [names, usedPaymentIds, form.pr_id, additionalParticipants]);

  const buildParticipantsFromDb = (pr_id, namesList) => {
    if (!pr_id || !namesList?.length) return [];
    return (Array.isArray(pr_id) ? pr_id : [])
      .map(id => {
        const p = namesList.find(n => String(n.payment_id) === String(id));
        if (!p) return null;
        return {
          pr_id: p.payment_id, full_name: p.billing_full_name,
          date_of_payment: p.date_of_payment, accommodation_status: p.accommodation_status,
          occupancy: p.occupancy_type, no_of_nights: p.total_no_of_nights, reg_category: p.reg_category,
        };
      }).filter(Boolean);
  };

  const getEditPreviewParticipants = () => {
    const existing = buildParticipantsFromDb(form.pr_id, names);
    return [...existing, ...additionalParticipants];
  };

  // ✅ total participant count used for limit display
  const totalParticipantCount = () => {
    if (formMode === 'add') return selectedParticipant ? 1 : 0;
    return getEditPreviewParticipants().length;
  };

  const buildMergedForm = (rowData) => {
    if (!conferenceInfo) return rowData;
    return {
      ...rowData,
      series: conferenceInfo.series, sub_domain: conferenceInfo.sub_domain,
      venue: conferenceInfo.venue, hotel_name: conferenceInfo.hotel_name,
      conf_shortname: conferenceInfo.conf_shortname,
    };
  };

  const parseNoteHistory = (note) => {
    try {
      let parsed = note;
      while (typeof parsed === 'string') parsed = JSON.parse(parsed);
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  };

  // ─── Form Actions (all unchanged) ────────────────────────────────────────

  const handleAddNew = async () => {
    try {
      const pmData = await getprogramManager({ sub_domain: subDomain });
      if (!pmData || pmData.length === 0) {
        Swal.fire({ icon: 'warning', title: 'Program Manager Not Found', text: 'No Program Manager details found. Please update the Program Manager details.' }); return;
      }
      if (pmData?.[0]?.status === false) {
        Swal.fire({ icon: 'warning', title: 'Program Manager Inactive', text: 'Your Program Manager details are inactive.' }); return;
      }
      if (!conferenceInfo) {
        Swal.fire({ icon: 'warning', title: 'No Conference Event Found', text: `No Conference Event details found for "${subDomain}". Please contact management.` }); return;
      }

      // ✅ Block Add if any conference field is missing
      const missingFields = [];
      if (!conferenceInfo.series)         missingFields.push('Series');
      if (!conferenceInfo.sub_domain)     missingFields.push('Sub Domain');
      if (!conferenceInfo.hotel_name)     missingFields.push('Hotel Name');
      if (!conferenceInfo.conf_shortname) missingFields.push('Shortname');
      if (!conferenceInfo.venue)          missingFields.push('Venue');

      if (missingFields.length > 0) {
        Swal.fire({
          icon: 'warning',
          title: 'Conference Details Incomplete',
          html: `The following Conference Event details are not updated:<br/><br/>
                <strong>${missingFields.join(', ')}</strong><br/><br/>
                Kindly contact the Admin to update the Conference Event details before adding accommodation.`,
          confirmButtonText: 'OK',
        });
        return; // ✅ Block form from opening
      }
      const newAccommodationId = await fetchReceiptId('accommodation');
      const freshForm = buildMergedForm({ ...INITIAL_FORM, accommodation_id: newAccommodationId });
      setErrors({}); setFormMode('add'); setSelectedParticipant(null);
      setAdditionalParticipants([]); setAddParticipantMode(false);
      setForm({ ...freshForm, note: '', notes: [] });
      setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Something went wrong while initializing the form.' });
    }
  };

  const handleEdit = (row) => {
    try {
      if (!conferenceInfo) {
        Swal.fire({ icon: 'warning', title: 'No Conference Event Found', text: `No Conference Event details found for "${subDomain}". Please contact management.` }); return;
      }
      const mergedForm = buildMergedForm({ ...row });
      const history    = parseNoteHistory(mergedForm.note);
      setErrors({}); setFormMode('edit'); setSelectedParticipant(null);
      setAdditionalParticipants([]); setAddParticipantMode(false);
      setForm({ ...mergedForm, note: '', notes: history });
      setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error Fetching Data', text: 'Something went wrong while retrieving accommodation details.' });
    }
  };

  const handleCancel = () => {
    setForm(INITIAL_FORM); setFormMode(''); setErrors({});
    setSelectedParticipant(null); setAdditionalParticipants([]); setAddParticipantMode(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedForm = { ...form, [name]: value };
    if (name === 'occupancy') {
      if (value === 'Single Occupancy') updatedForm.accompany_name = '';
      if ((value === 'Double Occupancy' || value === 'Triple Occupancy') && !form.accompany_name)
        updatedForm.accompany_name = 'TBA';
    }
    if ((name === 'check_in' && form.no_of_nights) || (name === 'no_of_nights' && form.check_in)) {
      const checkInDate = new Date(name === 'check_in' ? value : form.check_in);
      const nights      = Number(name === 'no_of_nights' ? value : form.no_of_nights);
      if (!isNaN(checkInDate) && nights) {
        const d = new Date(checkInDate);
        d.setDate(d.getDate() + nights);
        updatedForm.check_out = d.toISOString().split('T')[0];
      }
    }
    setForm(updatedForm);
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (formMode === 'add' && (!form.full_name || form.full_name.length === 0))
      newErrors.full_name = 'Payment receipt is required';
    if (!form.accommodation_id?.trim()) newErrors.accommodation_id = 'Accommodation ID is required';
    if (!form.first_name.trim())        newErrors.first_name       = 'First name is required';
    if (!form.last_name.trim())         newErrors.last_name        = 'Last name is required';
    if (!form.occupancy)                newErrors.occupancy        = 'Occupancy is required';
    if (!form.no_of_nights)             newErrors.no_of_nights     = 'No of nights is required';
    if ((form.occupancy === 'Double Occupancy' || form.occupancy === 'Triple Occupancy') && !form.accompany_name)
      newErrors.accompany_name = 'Accompany name is required';
    if (formMode === 'edit' && !form.note?.trim())
      newErrors.note = 'Note is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    let updatedForm = { ...form };
    let history = Array.isArray(form.notes) ? [...form.notes] : [];
    if (form.note && form.note.trim() !== '') {
      const now = new Date();
      const formatted =
        now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' +
        String(now.getDate()).padStart(2, '0') + ' ' +
        String(now.getHours()).padStart(2, '0') + ':' + String(now.getMinutes()).padStart(2, '0');
      history.push({ date: formatted, value: form.note.trim(), status: updatedForm.verified });
    }
    updatedForm.note = history.length ? JSON.stringify(history) : null;
    if (formMode === 'edit' && addParticipantMode && additionalParticipants.length)
      updatedForm.pr_id = [...(form.pr_id || []), ...additionalParticipants.map(p => p.pr_id)];
    if (!updatedForm.check_in && !updatedForm.check_out) updatedForm.verified = 'WAITING';
    else updatedForm.verified = 'PENDING';
    try {
      setSubmitLoading(true);
      if (formMode === 'add') await addData(updatedForm);
      else if (formMode === 'edit') await updateData(updatedForm);
      await fetchData();
      handleCancel();
      Swal.fire({
        icon: 'success', title: 'Success',
        text: `Accommodation ${formMode === 'add' ? 'added' : 'updated'} successfully!`,
        timer: 2000, showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Something went wrong while submitting the form!' });
    } finally { setSubmitLoading(false); }
  };

  const removeParticipant = (prId) => {
    setForm(prev => ({ ...prev, pr_id: (prev.pr_id || []).filter(id => String(id) !== String(prId)) }));
    setAdditionalParticipants(prev => prev.filter(p => String(p.pr_id) !== String(prId)));
  };

  const handleNameChange = (paymentId) => {
    const selectedPerson = names.find(p => String(p.payment_id) === String(paymentId));
    if (!selectedPerson) return;
    if (formMode === 'edit') {
      const total = (form.pr_id || []).length + additionalParticipants.length;
      if (total >= MAX_PARTICIPANTS) {
        Swal.fire({ icon: 'warning', title: 'Limit Reached', text: `Maximum ${MAX_PARTICIPANTS} participants allowed.` }); return;
      }
      setAdditionalParticipants(prev => [...prev, {
        full_name: selectedPerson.billing_full_name, date_of_payment: selectedPerson.date_of_payment,
        accommodation_status: selectedPerson.accommodation_status, pr_id: selectedPerson.payment_id,
        occupancy: selectedPerson.occupancy_type, no_of_nights: selectedPerson.total_no_of_nights,
        reg_category: selectedPerson.reg_category,
      }]);
    } else {
      setSelectedParticipant({
        pr_id: selectedPerson.payment_id, full_name: selectedPerson.billing_full_name,
        date_of_payment: selectedPerson.date_of_payment, accommodation_status: selectedPerson.accommodation_status,
        occupancy: selectedPerson.occupancy_type, no_of_nights: selectedPerson.total_no_of_nights,
        reg_category: selectedPerson.reg_category,
      });
      setForm(prev => ({
        ...prev, full_name: selectedPerson.billing_full_name, pr_id: [selectedPerson.payment_id],
        date_of_payment: selectedPerson.date_of_payment, accommodation_status: selectedPerson.accommodation_status,
        occupancy: selectedPerson.occupancy_type, no_of_nights: selectedPerson.total_no_of_nights,
        reg_category: selectedPerson.reg_category,
      }));
    }
  };

  const filteredData = useMemo(() =>
    dataList
      .filter(d =>
        d.first_name.toLowerCase().includes(searchText.toLowerCase())    ||
        d.last_name.toLowerCase().includes(searchText.toLowerCase())     ||
        d.accompany_name.toLowerCase().includes(searchText.toLowerCase())
      )
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)),
  [dataList, searchText]);

  const columns = [
    { name: '#',            selector: (row, i) => (currentPage - 1) * 10 + (i + 1), sortable: false, width: '60px' },
    { name: 'First Name',   selector: row => row.first_name,   sortable: true, wrap: true },
    { name: 'Last Name',    selector: row => row.last_name,    sortable: true, wrap: true },
    { name: 'Occupancy',    selector: row => row.occupancy,    sortable: true, wrap: true },
    { name: 'No of Nights', selector: row => row.no_of_nights, sortable: true, wrap: true },
    {
      name: 'Status', selector: row => row.verified, sortable: true,
      cell: row => (
        <span className={`rounded-pill px-3 py-1 text-white ${statusClassMap[row.verified] || statusClassMap.DEFAULT}`}>
          {row.verified || 'Unknown'}
        </span>
      ),
    },
    {
      name: 'Actions',
      cell: row => <Button variant="warning" size="sm" onClick={() => handleEdit(row)}>Edit</Button>,
    },
  ];

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Header />

        {loading ? (
          <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '300px' }}>
            <Spinner />
          </div>
        ) : (
          <div>
            <div className="container-fluid p-4">

              {/* ── Page Header ── */}
              <div className="row mb-3">
                <div className="col-8">
                  <p className="fs-4 text-dark mb-0">Conference Management / Accommodation Form</p>
                </div>
                <div className="col-4 text-end">
                  {formMode === '' && (
                    <button className="btn btn-primary" onClick={handleAddNew}>+ Add</button>
                  )}
                </div>
              </div>

              {/* ── Data Table ── */}
              {formMode === '' && (
                <DataTable
                  columns={columns}
                  data={filteredData}
                  pagination highlightOnHover responsive striped
                  paginationPerPage={10}
                  paginationRowsPerPageOptions={[10, 20, 50, 100, 200, 500]}
                  onChangePage={page => setCurrentPage(page)}
                  subHeader
                  subHeaderComponent={
                    <input
                      className="form-control w-25"
                      type="text" placeholder="Search"
                      value={searchText}
                      onChange={e => setSearchText(e.target.value)}
                    />
                  }
                />
              )}

              {/* ── Inline Form ── */}
              {formMode !== '' && (
                <div ref={formRef}>

                  {/* ════════════════════════════════════════
                      CARD 1 — Event Details
                  ════════════════════════════════════════ */}
                  <Card className="shadow-sm mb-3">
                    <Card.Header className="bg-secondary text-white">
                      <strong>Event Details</strong>
                    </Card.Header>
                    <Card.Body>
                      <div className="row">
                        <div className="col-md-6">
                          <table className="table table-sm table-bordered mb-0">
                            <tbody>
                              <tr>
                                <th style={{ width: '140px' }}>Venue</th>
                                <td>{form.venue || '-'}</td>
                              </tr>
                              <tr>
                                <th>Series</th>
                                <td>
                                  {form.series
                                    ? new Date(form.series + '-01').toLocaleString('en-US', { month: 'long', year: 'numeric' })
                                    : '-'}
                                </td>
                              </tr>
                              <tr>
                                <th>Subdomain</th>
                                <td>{form.sub_domain || '-'}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div className="col-md-6">
                          <table className="table table-sm table-bordered mb-0">
                            <tbody>
                              <tr>
                                <th style={{ width: '140px' }}>Hotel Name</th>
                                <td>{form.hotel_name || '-'}</td>
                              </tr>
                              <tr>
                                <th>Shortname</th>
                                <td>{form.conf_shortname || '-'}</td>
                              </tr>
                              <tr>
                                <th>Accommodation ID</th>
                                <td>
                                  <span className="badge bg-secondary">{form.accommodation_id || '-'}</span>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Confirmation / Remarks — edit only */}
                      {formMode === 'edit' && (form.confirmation_no || form.remarks) && (
                        <div className="row mt-3">
                          <div className="col-md-6">
                            <table className="table table-sm table-bordered mb-0">
                              <tbody>
                                <tr>
                                  <th style={{ width: '140px' }}>Confirmation No</th>
                                  <td>{form.confirmation_no || '-'}</td>
                                </tr>
                                <tr>
                                  <th>Remarks</th>
                                  <td>{form.remarks || '-'}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </Card.Body>
                  </Card>

                  {/* ════════════════════════════════════════
                      CARD 2 — Participant Details
                  ════════════════════════════════════════ */}
                  <Card className="shadow-sm mb-3">
                    <Card.Header className="bg-secondary text-white d-flex justify-content-between align-items-center">
                      <strong>Participant Details</strong>
                      {/* ✅ Participant counter badge */}
                      <Badge bg="light" text="dark">
                        {totalParticipantCount()} / {MAX_PARTICIPANTS} Participants
                      </Badge>
                    </Card.Header>
                    <Card.Body>

                      {/* Payment receipt dropdown — ADD mode always, EDIT mode when checkbox checked */}
                      {formMode === 'edit' && (
                        <div className="mb-3">
                          <Form.Check
                            type="checkbox"
                            label="Add More Participants"
                            checked={addParticipantMode}
                            onChange={e => {
                              setAddParticipantMode(e.target.checked);
                              if (!e.target.checked) setAdditionalParticipants([]);
                            }}
                          />
                        </div>
                      )}

                      {/* ✅ Dropdown — shown in add mode OR edit+checkbox, hidden when limit reached */}
                      {(formMode === 'add' ||
                        (formMode === 'edit' && addParticipantMode &&
                          (form.pr_id?.length || 0) + additionalParticipants.length < MAX_PARTICIPANTS)
                      ) && (
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <Form.Label className="fw-semibold">
                              Select Payment Receipt
                              {/* ✅ Limit reached message */}
                              {formMode === 'add' && selectedParticipant && (
                                <small className="text-muted ms-2">(1 / {MAX_PARTICIPANTS} selected)</small>
                              )}
                            </Form.Label>
                            <Form.Select
                              name="full_name"
                              value=""
                              onChange={e => handleNameChange(e.target.value)}
                              isInvalid={!!errors.full_name}
                              // ✅ Disable in add mode once 1 selected (single receipt for add)
                              disabled={formMode === 'add' && !!selectedParticipant}
                            >
                              <option value="">-- Select --</option>
                              {availableParticipants.map(item => (
                                <option key={item.payment_id} value={item.payment_id}>
                                  {item.billing_full_name} ({item.payment_id}){item.source === 'free' ? ' [Free]' : ''}
                                </option>
                              ))}
                              {availableParticipants.length === 0 && (
                                <option disabled>No payment receipt available</option>
                              )}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">{errors.full_name}</Form.Control.Feedback>

                            {/* ✅ Clear selection button — add mode only */}
                            {formMode === 'add' && selectedParticipant && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary mt-1"
                                onClick={() => {
                                  setSelectedParticipant(null);
                                  setForm(prev => ({ ...prev, full_name: '', pr_id: [] }));
                                }}
                              >
                                × Clear Selection
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* ✅ Limit reached warning */}
                      {formMode === 'edit' && addParticipantMode &&
                        (form.pr_id?.length || 0) + additionalParticipants.length >= MAX_PARTICIPANTS && (
                        <div className="alert alert-warning py-2 mb-3">
                          Maximum {MAX_PARTICIPANTS} participants reached. Remove a participant to add another.
                        </div>
                      )}

                      {/* ── ADD mode: single participant card ── */}
                      {formMode === 'add' && selectedParticipant && (
                        <div className="row">
                          <div className="col-md-4 mb-3">
                            <Card className="border shadow-sm h-100">
                              <Card.Header className="py-2 bg-light d-flex justify-content-between align-items-center">
                                <strong className="small">Participant 1</strong>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => {
                                    setSelectedParticipant(null);
                                    setForm(prev => ({ ...prev, full_name: '', pr_id: [] }));
                                  }}
                                >Remove</button>
                              </Card.Header>
                              <Card.Body className="p-2">
                                <table className="table table-sm table-bordered mb-0">
                                  <tbody>
                                    <tr><th>Payment ID</th>   <td>{selectedParticipant.pr_id}</td></tr>
                                    <tr><th>Full Name</th>    <td>{selectedParticipant.full_name}</td></tr>
                                    <tr><th>Date</th>         <td>{selectedParticipant.date_of_payment}</td></tr>
                                    <tr><th>Status</th>       <td>{selectedParticipant.accommodation_status}</td></tr>
                                    <tr><th>Occupancy</th>    <td>{selectedParticipant.occupancy}</td></tr>
                                    <tr><th>Nights</th>       <td>{selectedParticipant.no_of_nights}</td></tr>
                                    <tr><th>Category</th>     <td>{selectedParticipant.reg_category}</td></tr>
                                  </tbody>
                                </table>
                              </Card.Body>
                            </Card>
                          </div>
                        </div>
                      )}

                      {/* ── EDIT mode: participant cards (up to 5) ── */}
                      {formMode === 'edit' && (
                        <div className="row">
                          {getEditPreviewParticipants().map((p, i) => (
                            <div className="col-md-4 mb-3" key={p.pr_id}>
                              <Card className="border shadow-sm h-100">
                                <Card.Header className="py-2 bg-light d-flex justify-content-between align-items-center">
                                  <strong className="small">Participant {i + 1}</strong>
                                  {/* ✅ Remove only when more than 1 participant exists */}
                                  {getEditPreviewParticipants().length > 1 && (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => removeParticipant(p.pr_id)}
                                    >Remove</button>
                                  )}
                                </Card.Header>
                                <Card.Body className="p-2">
                                  <table className="table table-sm table-bordered mb-0">
                                    <tbody>
                                      <tr><th>Payment ID</th>   <td>{p.pr_id}</td></tr>
                                      <tr><th>Full Name</th>    <td>{p.full_name}</td></tr>
                                      <tr><th>Date</th>         <td>{p.date_of_payment}</td></tr>
                                      <tr><th>Status</th>       <td>{p.accommodation_status}</td></tr>
                                      <tr><th>Occupancy</th>    <td>{p.occupancy}</td></tr>
                                      <tr><th>Nights</th>       <td>{p.no_of_nights}</td></tr>
                                      <tr><th>Category</th>     <td>{p.reg_category}</td></tr>
                                    </tbody>
                                  </table>
                                </Card.Body>
                              </Card>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* ✅ Empty state — no participant selected yet */}
                      {formMode === 'add' && !selectedParticipant && (
                        <p className="text-muted small mb-0">
                          No participant selected. Use the dropdown above to select a payment receipt.
                        </p>
                      )}

                      <p className="text-muted small mt-2 mb-0">
                        <strong className="text-primary">Note:</strong> Split the full name into first and last name as per the confirmed participants.
                      </p>
                    </Card.Body>
                  </Card>

                  {/* ════════════════════════════════════════
                      CARD 3 — Accommodation Details
                  ════════════════════════════════════════ */}
                  <Card className="shadow-sm mb-3">
                    <Card.Header className="bg-secondary text-white">
                      <strong>Accommodation Details</strong>
                    </Card.Header>
                    <Card.Body>
                      <Form>
                        <div className="row">

                          <Form.Group className="col-md-3 mb-3">
                            <Form.Label>Accommodation ID <small className="text-danger">*</small></Form.Label>
                            <Form.Control
                              type="text" name="accommodation_id"
                              value={form.accommodation_id} readOnly
                              className="bg-light"
                              isInvalid={!!errors.accommodation_id}
                            />
                            <Form.Control.Feedback type="invalid">{errors.accommodation_id}</Form.Control.Feedback>
                          </Form.Group>

                          <Form.Group className="col-md-3 mb-3">
                            <Form.Label>First Name <small className="text-danger">*</small></Form.Label>
                            <Form.Control
                              type="text" name="first_name" placeholder="First name"
                              value={form.first_name} onChange={handleChange}
                              isInvalid={!!errors.first_name}
                            />
                            <Form.Control.Feedback type="invalid">{errors.first_name}</Form.Control.Feedback>
                          </Form.Group>

                          <Form.Group className="col-md-3 mb-3">
                            <Form.Label>Last Name <small className="text-danger">*</small></Form.Label>
                            <Form.Control
                              type="text" name="last_name" placeholder="Last name"
                              value={form.last_name} onChange={handleChange}
                              isInvalid={!!errors.last_name}
                            />
                            <Form.Control.Feedback type="invalid">{errors.last_name}</Form.Control.Feedback>
                          </Form.Group>

                          <Form.Group className="col-md-3 mb-3">
                            <Form.Label>Occupancy <small className="text-danger">*</small></Form.Label>
                            <Form.Control
                              as="select" className="form-select"
                              name="occupancy" value={form.occupancy}
                              onChange={handleChange} isInvalid={!!errors.occupancy}
                            >
                              <option value="">Select</option>
                              {dropdownData.occupancyType.map(o => (
                                <option key={o.type} value={o.type}>{o.type}</option>
                              ))}
                            </Form.Control>
                            <Form.Control.Feedback type="invalid">{errors.occupancy}</Form.Control.Feedback>
                          </Form.Group>

                          <Form.Group className="col-md-3 mb-3">
                            <Form.Label>No of Nights <small className="text-danger">*</small></Form.Label>
                            <Form.Control
                              type="number" name="no_of_nights" placeholder="0"
                              value={form.no_of_nights} onChange={handleChange}
                              isInvalid={!!errors.no_of_nights}
                            />
                            <Form.Control.Feedback type="invalid">{errors.no_of_nights}</Form.Control.Feedback>
                          </Form.Group>

                          <Form.Group className="col-md-3 mb-3">
                            <Form.Label>Check In</Form.Label>
                            <Form.Control
                              type="date" name="check_in"
                              value={form.check_in} onChange={handleChange}
                              isInvalid={!!errors.check_in}
                            />
                            <Form.Control.Feedback type="invalid">{errors.check_in}</Form.Control.Feedback>
                          </Form.Group>

                          <Form.Group className="col-md-3 mb-3">
                            <Form.Label>Check Out <small className="text-muted">(auto)</small></Form.Label>
                            <Form.Control
                              type="date" name="check_out"
                              value={form.check_out} readOnly
                              className="bg-light"
                              isInvalid={!!errors.check_out}
                            />
                            <Form.Control.Feedback type="invalid">{errors.check_out}</Form.Control.Feedback>
                          </Form.Group>

                          {form.occupancy !== 'Single Occupancy' && (
                            <Form.Group className="col-md-3 mb-3">
                              <Form.Label>Accompany Name</Form.Label>
                              <Form.Control
                                type="text" name="accompany_name" placeholder="Name or TBA"
                                value={form.accompany_name} onChange={handleChange}
                                isInvalid={!!errors.accompany_name}
                              />
                              <Form.Control.Feedback type="invalid">{errors.accompany_name}</Form.Control.Feedback>
                            </Form.Group>
                          )}

                        </div>

                        {/* Hidden fields */}
                        <Form.Control type="hidden" name="venue"                value={form.venue} />
                        <Form.Control type="hidden" name="series"               value={form.series} />
                        <Form.Control type="hidden" name="sub_domain"           value={form.sub_domain} />
                        <Form.Control type="hidden" name="hotel_name"           value={form.hotel_name} />
                        <Form.Control type="hidden" name="conf_shortname"       value={form.conf_shortname} />
                        <Form.Control type="hidden" name="pr_id"                value={JSON.stringify(form.pr_id)} />
                        <Form.Control type="hidden" name="full_name"            value={form.full_name} />
                        <Form.Control type="hidden" name="accommodation_status" value={form.accommodation_status} />
                      </Form>
                    </Card.Body>
                  </Card>

                  {/* ════════════════════════════════════════
                      CARD 4 — Notes & History
                  ════════════════════════════════════════ */}
                  <Card className="shadow-sm mb-3">
                    <Card.Header className="bg-light">
                      <strong>Notes</strong>
                    </Card.Header>
                    <Card.Body>

                      {/* Notes timeline — shown when history exists */}
                      {form.notes?.length > 0 && (
                        <div className="mb-3" style={{ maxHeight: '260px', overflowY: 'auto' }}>
                          <p className="text-muted small fw-semibold mb-2">Recent Changes</p>
                          {form.notes.slice().reverse().map((n, i, arr) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '10px' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '20px', marginRight: '10px', flexShrink: 0 }}>
                                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#6c757d', marginTop: '3px' }} />
                                {i !== arr.length - 1 && (
                                  <div style={{ width: '2px', background: '#dee2e6', marginTop: '3px', minHeight: '28px' }} />
                                )}
                              </div>
                              <div>
                                <div className="small mb-1">{n.value}</div>
                                {n.status && (
                                  <span className={`badge me-1 text-white ${statusClassMap[n.status] || statusClassMap.DEFAULT}`}>
                                    {n.status}
                                  </span>
                                )}
                                <div className="text-muted" style={{ fontSize: '0.75rem' }}>{n.date}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      <Form.Group>
                        <Form.Label>
                          Add Note
                          {formMode === 'edit' && <small className="text-danger ms-1">*</small>}
                        </Form.Label>
                        <Form.Control
                          as="textarea" rows={3} name="note"
                          placeholder={formMode === 'edit' ? 'Describe what changed...' : 'Optional note'}
                          value={form.note || ''} onChange={handleChange}
                          isInvalid={!!errors.note}
                        />
                        <Form.Control.Feedback type="invalid">{errors.note}</Form.Control.Feedback>
                      </Form.Group>
                    </Card.Body>
                  </Card>

                  {/* ── Form Action Buttons ── */}
                  <div className="d-flex justify-content-end gap-2 mb-4">
                    <Button variant="secondary" onClick={handleCancel} disabled={submitLoading}>
                      Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSubmit} disabled={submitLoading}>
                      {submitLoading
                        ? <><span className="spinner-border spinner-border-sm me-2" />Processing...</>
                        : formMode === 'add' ? 'Add' : 'Update'
                      }
                    </Button>
                  </div>

                </div>
              )}

            </div>
            <Footer />
          </div>
        )}
      </div>
    </div>
  );
};

export default Accommodation;